<template>
    <BaseCard :theme="isSelected ? 'muted' : 'default'" padding="md" class="relative w-full min-w-0 flex flex-col gap-2" :class="{ 'bg-surface-hover': isLinkedHover }">
        <BaseButton
            theme="tertiary"
            class="absolute inset-0 h-full w-full"
            :aria-label="t('journeys.goalCard.viewDetails', { title: goal.title })"
            :aria-describedby="actionLabelId"
            :aria-pressed="!!isSelected"
            @click="emit('select', goal)" />
        <!-- Suggested badge -->
        <div v-if="isSuggestion" class="relative pointer-events-none flex items-center gap-1 self-start text-ink-muted">
            <span class="details">{{ suggestionLabel }}</span>
            <i class="bi bi-stars text-xs" aria-hidden="true"></i>
        </div>

        <!-- Title row -->
        <div class="relative pointer-events-none flex items-center gap-2 min-w-0">
            <span class="details text-ink truncate flex-1 min-w-0">{{ goal.title }}</span>
        </div>

        <!-- Bottom row: action text (+progress bar) on the left, CTAs on the right, inline -->
        <div class="relative pointer-events-none flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-col gap-1 min-w-0 flex-1">
                <div class="flex items-center gap-2 min-w-0">
                    <p :id="actionLabelId" class="details text-ink-muted">{{ actionLabel }}</p>
                </div>
                <BaseProgressBar
                    v-if="totalActions > 0"
                    :value="completedActions"
                    :max="totalActions"
                    size="xs"
                    :aria-label="t('journeys.goalCard.actionsCompleted', { title: goal.title })" />
            </div>
            <div v-if="hasCta" class="pointer-events-auto flex flex-wrap items-center gap-2 shrink-0">
                <!-- Assign CTA: opens the assign-to-team-member modal (never the goal sidebar) -->
                <BaseButton
                    v-if="showAssignButton"
                    theme="secondary"
                    size="sm"
                    class="shrink-0 inline-flex items-center gap-1"
                    :aria-label="t('journeys.goalCard.assign')"
                    @click="emit('assign', goal.id)">
                    <i class="bi bi-person-plus text-sm leading-none" aria-hidden="true"></i>
                </BaseButton>

                <!-- Dive Deeper CTA: shown on prioritized goals -->
                <BaseButton
                    v-if="showDiscussButton"
                    theme="secondary"
                    size="sm"
                    class="shrink-0"
                    :disabled="isDiscussLoading"
                    :aria-busy="isDiscussLoading"
                    :aria-label="t('journeys.goalCard.diveDeeperAria')"
                    @click="emit('discuss', goal.id)">
                    <span class="relative inline-flex items-center justify-center">
                        <LoadingSpinner v-if="isDiscussLoading" class="h-4 w-4 absolute" />
                        <span :class="{ invisible: isDiscussLoading }">{{ t("journeys.goalCard.diveDeeper") }}</span>
                    </span>
                </BaseButton>
            </div>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { type Goal } from "~vue/types/goals";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    goal: Goal;
    isLoading?: boolean;
    isSelected?: boolean;
    isSuggestion?: boolean;
    isLinkedHover?: boolean;
    showAssignButton?: boolean;
    showDiscussButton?: boolean;
    isDiscussLoading?: boolean;
}>();

const emit = defineEmits<{
    updated: [goalId: string];
    select: [goal: Goal];
    assign: [goalId: string];
    discuss: [goalId: string];
}>();

const { t } = useI18n();

const actionLabelId = useId();

const hasCta = computed(() => {
    if (props.showAssignButton) {
        return true;
    }
    if (props.showDiscussButton) {
        return true;
    }
    return false;
});

const suggestionLabel = computed(() => (props.goal.source === "assessment" ? t("journeys.goalCard.suggestedFromReflection") : t("journeys.goalCard.suggestedFromChat")));

const completedActions = computed(() => props.goal.actions?.filter((a) => a.status === "completed").length ?? 0);
const totalActions = computed(() => props.goal.actions?.length ?? 0);
const actionLabel = computed(() => {
    const total = totalActions.value;
    return total === 0 ? t("journeys.goalCard.noActions") : t("journeys.goalCard.actionsCompletedCount", { completed: completedActions.value, total });
});
</script>
